import type { Theme } from "../types/Theme";
import * as Mixins from "./Mixins";
import { shouldUseModernTheme } from "./navigationButtonThemeColors";

/** Modern security-question chrome only when {@link Theme.UseModernTheme} is strictly true. */
export function useModernSecurityQuestionStyles(theme: Theme): boolean {
    return shouldUseModernTheme(theme);
}

/** Title / instruction block-start padding (px); modern uses within-component spacing. */
export function resolveSecurityQuestionTitlePaddingY(
    theme: Theme,
): string | number[][] {
    if (!useModernSecurityQuestionStyles(theme)) {
        return "5px 0";
    }
    return [[Mixins.getSpacing(theme, 0, theme.QuestionSpacing?.WithinComponents ?? 0), 0]];
}

/**
 * Instruction text padding; modern matches QuestionRenderer `questionText`
 * (`getSpacing(theme, 0, 8)`), not WithinComponents — same fixed title-to-body gap.
 */
export function resolveSecurityQuestionTextPadding(
    theme: Theme,
): string | number[][] {
    if (!useModernSecurityQuestionStyles(theme)) {
        return "10px 0";
    }
    return [[Mixins.getSpacing(theme, 0, 8), 0]];
}

/** Outer container vertical padding. */
export function resolveSecurityQuestionContainerPadding(
    theme: Theme,
): string | number[][] {
    if (!useModernSecurityQuestionStyles(theme)) {
        return "20px 0";
    }
    const within = Mixins.getSpacing(theme, 20, theme.QuestionSpacing?.WithinComponents ?? 0);
    return [[within, 0]];
}

/** Gap below select / question row. */
export function resolveSecurityQuestionFieldMarginBottom(theme: Theme): string | number {
    if (!useModernSecurityQuestionStyles(theme)) {
        return "15px";
    }
    return Mixins.getSpacing(theme, 15, theme.QuestionSpacing?.WithinComponents ?? 0);
}

/** Gap below answer input. */
export function resolveSecurityQuestionAnswerMarginBottom(theme: Theme): string | number {
    if (!useModernSecurityQuestionStyles(theme)) {
        return "20px";
    }
    return Mixins.getSpacing(theme, 20, theme.QuestionSpacing?.WithinComponents ?? 0);
}

export function resolveSecurityQuestionTitleFontSize(theme: Theme): string | undefined {
    if (!useModernSecurityQuestionStyles(theme)) {
        return undefined;
    }
    return Mixins.getFontSize(theme.TitleFontSize, Mixins.getFontSize(theme.TextFontSize));
}

export function resolveSecurityQuestionTextFontSize(theme: Theme): string | undefined {
    if (!useModernSecurityQuestionStyles(theme)) {
        return undefined;
    }
    return Mixins.getFontSize(theme.TextFontSize);
}

export function resolveSecurityQuestionForeground(theme: Theme): string | undefined {
    if (!useModernSecurityQuestionStyles(theme)) {
        return undefined;
    }
    return Mixins.foregroundColor(theme, Mixins.wrapperBackgroundColor(theme));
}
